import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { Outlet } from 'react-router';
import { isNil } from 'ramda';
import moment from 'moment';
import { useAppDispatch } from '@/store/hooks';
import { NavigationProvider } from '@/navigation/navigation.provider';
import { useGetToken } from '@/auth/hooks/authentication';
import InactivityMonitor from '@/components/inactivity-monitor';
import { ANALYTICS_KEY, AnalyticsDashboard, AnalyticsProvider, DEFAULT_DASHBOARD_TYPE, isApiUsageDashboardVisible, isBeaconDashboardVisible, normalizeDashboardTitle, } from '@/search-for-retail/analytics';
import { useGetCollections } from '@/platform-services/collections';
import { useLazyGetAnalyticsFolder } from '@/platform-services/analytics';
import { addError } from '@/store/slices/notificationsSlice';
import { storeAllCollections } from '@/store/slices/collectionsSlice';
import { storeAllAreas } from '@/store/slices/areasSlice';
import { getAreaList } from '@/search-for-retail/area/area.client';
import { getEnvSubdomainFromHostname } from '@/client/utils';
import { MainSectionWrapper } from '@/components/content-section';
import { usePathname, useShouldDisplayAnalytics } from '@/routing/hooks/utils';
import { createDrawerConfig } from '@/navigation/drawer/drawer.config';
import { useActiveUser } from '@/platform-services/auth';
import { useTenantIdSelector } from '@/store/selectors';
import { DrawerContainer } from '@/navigation/drawer';
import { logError } from '@/utils/logger';
import { useHandleModelTypeOnLocationChanged, useUpdateAreaOnLocationChanged } from '@/routing/hooks';
import { getValidatedModelPath } from '@/routing';
import { FeatureFlag, useFeatureFlagsProvider } from '@/search-for-retail/feature-flags';
import { SimpleLoading } from '@/components/loading.component';
import { useSessionAreaName } from '@/platform-services/session';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { getConfig } from '@/config';
import { getTenantName } from '@/utils/tenant';
export const LoggedInLayout = () => {
    var _a, _b;
    const dispatch = useAppDispatch();
    const [isPendoInitialized, setIsPendoInitialized] = useState(false);
    const [dashboards, setDashboards] = useState([]);
    const [currentDashboardType, setCurrentDashboardType] = useState(DEFAULT_DASHBOARD_TYPE);
    const [areDashboardsLoading, setAreDashboardsLoading] = useState(true);
    const { featureFlagsService } = useFeatureFlagsProvider();
    const isNewSearchPerformanceDashboard = featureFlagsService.isFeatureEnabled(FeatureFlag.EnableNewSearchPerformanceDashboard);
    const isBeaconValidationEnabled = featureFlagsService.isFeatureEnabled(FeatureFlag.EnableBeaconValidationDashboard);
    // use logged-in tenant identity; fall back to deployment-time customer for per-customer deployments
    const currentCustomer = ((_a = getTenantName()) === null || _a === void 0 ? void 0 : _a.trim()) || getConfig().customer;
    const drawerConfig = useMemo(() => (createDrawerConfig({
        isNewSearchPerformanceDashboard,
    })), [isNewSearchPerformanceDashboard]);
    const pathname = usePathname();
    const getToken = useGetToken();
    const modelPath = getValidatedModelPath(pathname);
    const shouldDisplayAnalytics = useShouldDisplayAnalytics(modelPath);
    const activeAreaName = useSessionAreaName();
    const [isAreaLoading, setIsAreaLoading] = useState(false);
    const { triggerGetAnalyticsFolder } = useLazyGetAnalyticsFolder();
    useHandleModelTypeOnLocationChanged();
    useUpdateAreaOnLocationChanged();
    const setAnalyticsDashboardType = useCallback((dashboardType) => {
        setCurrentDashboardType(dashboardType);
    }, []);
    const { data: collectionsData, isError: isCollectionsError, error: collectionsError } = useGetCollections();
    // Sync RTK Query collections data into the legacy collectionsSlice for backward compat
    useEffect(() => {
        if (collectionsData) {
            dispatch(storeAllCollections(collectionsData));
        }
    }, [collectionsData, dispatch]);
    useEffect(() => {
        if (isCollectionsError) {
            const errorMessage = 'TASK_COLLECTIONS_LOAD_ALL_ERROR';
            logError(errorMessage, collectionsError);
            dispatch(addError({ key: errorMessage }));
        }
    }, [isCollectionsError, collectionsError, dispatch]);
    const loadAreas = useCallback((token) => __awaiter(void 0, void 0, void 0, function* () {
        if (!token)
            return;
        try {
            const data = yield getAreaList();
            dispatch(storeAllAreas(data));
        }
        catch (e) {
            const errorMessage = 'TASK_AREA_LOAD_ALL_ERROR';
            logError(errorMessage, e);
            dispatch(addError({ key: errorMessage }));
        }
    }), [dispatch]);
    const user = useActiveUser();
    const tenantId = useTenantIdSelector();
    const { consentGiven } = useCookieConsent();
    useEffect(() => {
        var _a, _b;
        if (!window.pendo || !user)
            return;
        if (consentGiven !== 'accepted') {
            if (isPendoInitialized) {
                (_b = (_a = window.pendo).clearSession) === null || _b === void 0 ? void 0 : _b.call(_a);
                setIsPendoInitialized(false);
            }
            return;
        }
        if (isPendoInitialized)
            return;
        setIsPendoInitialized(true);
        window.pendo.initialize({
            visitor: {
                id: user.id,
                email: user.email,
                full_name: user.name,
            },
            account: {
                id: tenantId,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user === null || user === void 0 ? void 0 : user.id, consentGiven]);
    useEffect(() => {
        function action() {
            return __awaiter(this, void 0, void 0, function* () {
                setIsAreaLoading(true);
                const token = yield getToken();
                yield loadAreas(token);
                setIsAreaLoading(false);
            });
        }
        void action();
    }, [dispatch, loadAreas, getToken]);
    // fetch analytics dashboards folders
    useEffect(() => {
        const action = () => __awaiter(void 0, void 0, void 0, function* () {
            const token = yield getToken();
            if (!token || isNil(activeAreaName) || isAreaLoading) {
                setAreDashboardsLoading(false);
                return;
            }
            setAreDashboardsLoading(true);
            let folder;
            const envSubdomain = getEnvSubdomainFromHostname(window.location.hostname);
            const analyticsCacheKey = `${ANALYTICS_KEY}.${envSubdomain}_v3`;
            try {
                const folderCacheStr = localStorage.getItem(analyticsCacheKey);
                if (!isNil(folderCacheStr)) {
                    const folderCache = JSON.parse(folderCacheStr);
                    const isWithin7Days = moment(folderCache.dateRead).add(7, 'days').isAfter(new Date());
                    if (isWithin7Days) {
                        folder = folderCache.folder;
                    }
                }
                if (isNil(folder)) {
                    folder = yield triggerGetAnalyticsFolder();
                    localStorage.setItem(analyticsCacheKey, JSON.stringify({
                        dateRead: new Date(),
                        folder,
                    }));
                }
                // prevent WSOD when dashboards is set to undefined for certain tenants or bad account setups
                if (!isNil(folder.dashboards)) {
                    const filteredDashboards = folder.dashboards.filter((d) => isBeaconDashboardVisible(d.title, isBeaconValidationEnabled)
                        && isApiUsageDashboardVisible(d.title, currentCustomer));
                    setDashboards(filteredDashboards);
                }
            }
            catch (e) {
                logError('failed to load dashboards', e);
            }
            finally {
                setAreDashboardsLoading(false);
            }
        });
        void action();
    }, [activeAreaName, isAreaLoading, isBeaconValidationEnabled, currentCustomer, getToken, triggerGetAnalyticsFolder]);
    if (isAreaLoading || areDashboardsLoading) {
        return <SimpleLoading />;
    }
    const currentDashboard = (_b = dashboards.find((dashboard) => normalizeDashboardTitle(dashboard.title) === currentDashboardType)) !== null && _b !== void 0 ? _b : dashboards[0];
    return (<React.Fragment>
      <AnalyticsProvider>
        <NavigationProvider dashboards={dashboards} drawerConfig={drawerConfig}>
          <DrawerContainer displayAnalytics={shouldDisplayAnalytics}>
            <MainSectionWrapper>
              <Outlet context={{ setAnalyticsDashboardType }}/>
            </MainSectionWrapper>
          </DrawerContainer>
          {(shouldDisplayAnalytics && currentDashboard && activeAreaName) && <AnalyticsDashboard dashboard={currentDashboard}/>}
        </NavigationProvider>
      </AnalyticsProvider>
      <InactivityMonitor />
    </React.Fragment>);
};
